import type { ChannelConnection } from '@sbc/domain-models/gateway';

import { Badge } from '@/components/ui/badge';
import { CHANNEL_CREDENTIALS, CREDENTIAL_LABELS } from '@/routes/gateway/channel-credentials';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <dt className="text-sm font-medium">{label}</dt>
      <dd className="text-sm text-[var(--color-muted-foreground)]">{value}</dd>
    </div>
  );
}

function SecretRow({ label, isSet }: { label: string; isSet: boolean }) {
  return (
    <div className="space-y-1">
      <dt className="text-sm font-medium">{label}</dt>
      <dd>
        {isSet ? (
          <Badge variant="secondary">Configured</Badge>
        ) : (
          <Badge variant="outline">Not set</Badge>
        )}
      </dd>
    </div>
  );
}

// Reports only the credentials this channel reads, gated on the same map the connection form
// gates its inputs on. A row the form refuses to offer must not appear here either: "Access
// token — Not set" on an `sbc` connection reads as broken configuration when that channel
// stores no credential at all.
export function ConnectionAccountFields({ connection }: { connection: ChannelConnection }) {
  const credentials = CHANNEL_CREDENTIALS[connection.channel];
  const labels = CREDENTIAL_LABELS[connection.channel];

  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      <Field label={labels.account} value={connection.externalAccountId} />
      {credentials.actor && (
        <Field label={labels.actor} value={connection.externalActorId ?? 'not set'} />
      )}
      {credentials.verifyToken && (
        <SecretRow label="Webhook verify token" isSet={connection.verifyTokenSet} />
      )}
      {credentials.appSecret && (
        <SecretRow label={labels.appSecret} isSet={connection.appSecretSet} />
      )}
      {credentials.accessToken && (
        <SecretRow label={labels.accessToken} isSet={connection.accessTokenSet} />
      )}
      {credentials.oauthClient && (
        <Field label="OAuth client id" value={connection.oauthClientId ?? 'not set'} />
      )}
      {credentials.oauthClient && (
        <SecretRow label="OAuth client secret" isSet={connection.oauthClientSecretSet} />
      )}
      {/* Only Instagram's token is short-lived and refreshed on a schedule (token-refresh.job). */}
      {connection.channel === 'instagram' && (
        <Field
          label="Access token expires"
          value={
            connection.tokenExpiresAt
              ? new Date(connection.tokenExpiresAt).toLocaleString()
              : 'unknown'
          }
        />
      )}
    </dl>
  );
}
